import { useIoaStore } from "@/stores/ioa-store";
import { useTagStore } from "@/stores/tags-store";
import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { usePointStore } from "@/stores/point-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useRetentionStore } from "@/stores/retention-store";
import { useQuarantineStore } from "@/stores/quarantine-store";
import { useAnnotationStore } from "@/stores/annotation-store";
import { useEvidenceCountStore } from "@/stores/evidence-count-store";

import { pollingEvents } from "@/stores/plugins/polling-events";

import type { Api } from "@/services/Api";

let endTime = 0;
let currentTier = 0;
let pollingTimerId: number | null = null;

export let PollingAbortController: AbortController | null = null;

export function pollingPlugin() {
  useCaseStore().$onAction((ctx) => {
    if (ctx.name !== "onCaseReferenceLoaded") return;

    // Restart timer whenever caseReference changes
    if (ctx.store.selectedCaseName) {
      // Start at the highest tier
      currentTier = getPollingConfig().length - 1;
      startTimer();
    } else {
      // We've unloaded the case, we should stop polling
      stopTimer();
    }
  });
}

/** Specifies the endpoints/stores to be reloaded after the interval timeout */
async function refreshStores() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type IGNORE_TYPE = Api;
  /**
   * GET requests in this array should probably use the {@link Api.getWithPollingSignal} helper,
   * to cancel the requests when the interval is restarted
   */
  const promises: Array<Promise<void>> = [
    useMetadataStore().refresh(),
    useQuarantineStore().refresh(),
    useIoaStore().load(),
    useEvidenceCountStore().load(),
    useTagStore().loadFileTags(),
    useAnnotationStore().load(),
    usePointStore().load(),
    useRetentionStore().load(true),
    useCaseStore().loadMigrationPlans(),
  ];

  await Promise.allSettled(promises);
}

async function timer() {
  const selectedCaseName = useCaseStore().selectedCaseName;

  if (selectedCaseName != null) {
    const remaining = Math.max(0, Math.round((endTime - Date.now()) / 1000));

    if (remaining > 0) {
      pollingTimerId = requestAnimationFrame(() => void timer());
      return;
    }

    await refreshStores();

    currentTier = Math.min(++currentTier, getPollingConfig().length - 1);
    startTimer();
  } else {
    // No caseReference, stop timers
    stopTimer();
  }
}

function stopTimer() {
  if (PollingAbortController != null) {
    PollingAbortController?.abort();
    PollingAbortController = null;
  }

  if (pollingTimerId != null) {
    cancelAnimationFrame(pollingTimerId);
    pollingTimerId = null;
  }
}

function startTimer() {
  // Has to be done before calling `stopTimer`
  const isTimerStopped = pollingTimerId == null;

  stopTimer();
  PollingAbortController = new AbortController();

  // Start our polling at the current tier
  const timeout = getPollingConfig()[currentTier];

  endTime = Date.now() + timeout * 1e3;
  console.debug(`${isTimerStopped ? "Starting poll. " : ""}Next refresh in ${timeout} seconds`);

  pollingTimerId = requestAnimationFrame(() => void timer());
}

/** Gets the interval tiers to poll against */
function getPollingConfig() {
  const polling = useBrandStore().viewerConfig.polling ?? [];
  if (polling.length < 1) return [2, 5, 15, 15 * 60];
  return polling;
}

pollingEvents.restart = function () {
  if (import.meta.env.DEV) {
    console.groupCollapsed("Restarting polling");
    console.trace();
    console.groupEnd();
  }

  currentTier = 0;
  startTimer();
};
